import type { RowPinningState } from '@tanstack/react-table';
import type {
  MRT_ColumnDef,
  MRT_ColumnOrderState,
  MRT_RowData,
  MRT_SortingState,
  MRT_Updater,
  MRT_VisibilityState,
} from 'material-react-table';
import { useMemo, useState } from 'react';

const TABLE_PREFS_STORAGE_PREFIX = 'fusion.baseTable.tablePrefs.v1';

export type BaseTablePreferences = {
  columnOrder: MRT_ColumnOrderState;
  columnVisibility: MRT_VisibilityState;
  sorting: MRT_SortingState;
  rowPinning: RowPinningState;
};

export type BaseTableState = Partial<BaseTablePreferences>;

export type BaseTableColumnOption = {
  id: string;
  label: string;
  required: boolean;
};

/**
 * A BaseTable column definition.
 *
 * `columnDefType` is deliberately widened from MRT's `'data' | 'display' | 'group'`
 * to `string`. Callers build their column arrays as plain object literals - see
 * AlarmsTable.tsx and ServiceManager.index.tsx - so `columnDefType: 'display'`
 * widens to `string` at the call site and would not satisfy MRT's union without
 * every caller adding `as const` or an explicit annotation. BaseTable therefore
 * casts back to MRT_ColumnDef when handing columns to MaterialReactTable.
 *
 * This is the one intentional type hole in the table subsystem. Narrowing it
 * means annotating the caller-side column arrays, which is worth doing but is a
 * larger change than the conversion itself.
 */
export type BaseTableColumnDef<TData extends MRT_RowData> = Omit<
  MRT_ColumnDef<TData, any>,
  'columnDefType'
> & {
  columnDefType?: string;
};

type BaseTablePreferenceHandlers = {
  onColumnOrderChange?: (updater: MRT_Updater<MRT_ColumnOrderState>) => void;
  onColumnVisibilityChange?: (updater: MRT_Updater<MRT_VisibilityState>) => void;
  onRowPinningChange?: (updater: MRT_Updater<RowPinningState>) => void;
  onSortingChange?: (updater: MRT_Updater<MRT_SortingState>) => void;
};

type UseBaseTablePreferencesProps<TData extends MRT_RowData> = BaseTablePreferenceHandlers & {
  columns: BaseTableColumnDef<TData>[];
  initialState: BaseTableState;
  state?: BaseTableState;
  tablePreferenceKey?: string;
};

const resolveStateUpdater = <T>(updater: T | ((old: T) => T), currentValue: T): T => {
  if (typeof updater === 'function') {
    return (updater as (old: T) => T)(currentValue);
  }

  return updater;
};

export { resolveStateUpdater };

const getColumnId = <TData extends MRT_RowData>(
  column: BaseTableColumnDef<TData>,
): string | null => {
  const accessorKey = column.accessorKey;

  if (column.id) return `${column.id}`;
  if (typeof accessorKey === 'string') return accessorKey;
  if (typeof column.header === 'string') return column.header;

  return null;
};

const getColumnLabel = <TData extends MRT_RowData>(
  column: BaseTableColumnDef<TData>,
  id: string,
): string => {
  if (typeof column.header === 'string' && column.header.trim()) return column.header;
  return id;
};

const hashString = (value: string): string => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
};

const loadTablePreferences = (storageKey: string): Partial<BaseTablePreferences> => {
  if (typeof window === 'undefined') return {};

  try {
    return JSON.parse(window.localStorage.getItem(storageKey) || '{}') || {};
  } catch {
    return {};
  }
};

const saveTablePreferences = (storageKey: string, preferences: BaseTablePreferences): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preferences));
    return true;
  } catch {
    return false;
  }
};

const normalizeTablePreferences = (
  preferences: Partial<BaseTablePreferences> = {},
  columnOptions: BaseTableColumnOption[] = [],
  initialState: BaseTableState = {},
): BaseTablePreferences => {
  const defaultOrder = columnOptions.map((column) => column.id);
  const validOrderIds = new Set(defaultOrder);
  const storedOrder = Array.isArray(preferences.columnOrder)
    ? preferences.columnOrder
    : initialState.columnOrder || [];
  const columnOrder = [
    ...storedOrder.filter((columnId) => validOrderIds.has(columnId)),
    ...defaultOrder.filter((columnId) => !storedOrder.includes(columnId)),
  ];
  const requiredVisibility = Object.fromEntries(
    columnOptions.filter((column) => column.required).map((column) => [column.id, true]),
  );

  return {
    columnOrder,
    columnVisibility: {
      ...(initialState.columnVisibility || {}),
      ...(preferences.columnVisibility || {}),
      ...requiredVisibility,
    },
    sorting: Array.isArray(preferences.sorting)
      ? preferences.sorting
      : Array.isArray(initialState.sorting)
        ? initialState.sorting
        : [],
    rowPinning: {
      top: Array.isArray(preferences.rowPinning?.top)
        ? preferences.rowPinning.top
        : initialState.rowPinning?.top || [],
      bottom: Array.isArray(preferences.rowPinning?.bottom)
        ? preferences.rowPinning.bottom
        : initialState.rowPinning?.bottom || [],
    },
  };
};

/** Return the configurable columns exposed by a BaseTable. */
export const getBaseTableColumnOptions = <TData extends MRT_RowData>(
  columns: BaseTableColumnDef<TData>[],
): BaseTableColumnOption[] =>
  columns
    .map((column) => {
      const id = getColumnId(column);
      if (!id) return null;

      return {
        id,
        label: getColumnLabel(column, id),
        required: column.enableHiding === false,
      };
    })
    .filter((column): column is BaseTableColumnOption => column !== null);

/** Return column options in the persisted table order. */
export const getOrderedColumnOptions = (
  columnOptions: BaseTableColumnOption[],
  columnOrder: MRT_ColumnOrderState,
): BaseTableColumnOption[] => {
  const byId = new Map(columnOptions.map((column) => [column.id, column]));
  const ordered = columnOrder
    .map((columnId) => byId.get(columnId))
    .filter((column): column is BaseTableColumnOption => column !== undefined);
  const orderedIds = new Set(ordered.map((column) => column.id));
  const missing = columnOptions.filter((column) => !orderedIds.has(column.id));

  return [...ordered, ...missing];
};

/** Manage persisted and externally controlled BaseTable preferences. */
export const useBaseTablePreferences = <TData extends MRT_RowData>({
  columns,
  initialState,
  onColumnOrderChange,
  onColumnVisibilityChange,
  onRowPinningChange,
  onSortingChange,
  state,
  tablePreferenceKey,
}: UseBaseTablePreferencesProps<TData>) => {
  const columnOptions = useMemo(() => getBaseTableColumnOptions(columns), [columns]);
  const storageKey = useMemo(() => {
    if (tablePreferenceKey) return `${TABLE_PREFS_STORAGE_PREFIX}.${tablePreferenceKey}`;

    const routeKey =
      typeof window === 'undefined' ? 'default' : window.location?.pathname || 'default';
    const columnKey = columnOptions.map((column) => column.id).join('|');

    return `${TABLE_PREFS_STORAGE_PREFIX}.${hashString(`${routeKey}:${columnKey}`)}`;
  }, [columnOptions, tablePreferenceKey]);
  const [internalPreferences, setInternalPreferences] = useState<BaseTablePreferences>(() =>
    normalizeTablePreferences(loadTablePreferences(storageKey), columnOptions, initialState),
  );
  const tablePreferences = normalizeTablePreferences(
    {
      ...internalPreferences,
      ...state,
    },
    columnOptions,
    initialState,
  );

  const updateInternalPreferences = (updater: MRT_Updater<BaseTablePreferences>): void => {
    setInternalPreferences((currentPreferences) => {
      const nextPreferences = resolveStateUpdater(updater, currentPreferences);
      return normalizeTablePreferences(nextPreferences, columnOptions, initialState);
    });
  };

  const setColumnVisibility = (updater: MRT_Updater<MRT_VisibilityState>): void => {
    const nextVisibility = resolveStateUpdater(updater, tablePreferences.columnVisibility);
    updateInternalPreferences((currentPreferences) => ({
      ...currentPreferences,
      columnVisibility: nextVisibility,
    }));
    onColumnVisibilityChange?.(updater);
  };

  const setColumnOrder = (updater: MRT_Updater<MRT_ColumnOrderState>): void => {
    const nextOrder = resolveStateUpdater(updater, tablePreferences.columnOrder);
    updateInternalPreferences((currentPreferences) => ({
      ...currentPreferences,
      columnOrder: nextOrder,
    }));
    onColumnOrderChange?.(updater);
  };

  const setSorting = (updater: MRT_Updater<MRT_SortingState>): void => {
    const nextSorting = resolveStateUpdater(updater, tablePreferences.sorting);
    updateInternalPreferences((currentPreferences) => ({
      ...currentPreferences,
      sorting: nextSorting,
    }));
    onSortingChange?.(updater);
  };

  const setRowPinning = (updater: MRT_Updater<RowPinningState>): void => {
    const nextRowPinning = resolveStateUpdater(updater, tablePreferences.rowPinning);
    updateInternalPreferences((currentPreferences) => ({
      ...currentPreferences,
      rowPinning: nextRowPinning,
    }));
    onRowPinningChange?.(updater);
  };

  return {
    columnOptions,
    saveTablePreferences,
    setColumnOrder,
    setRowPinning,
    setColumnVisibility,
    setSorting,
    storageKey,
    tablePreferences,
  };
};
